import type {
  HomeFilterCategory,
  HomeFilterData,
  HomeFilterEntry,
  HomeFilters,
  PhotoPayload,
} from '@/constant/types'
import { getMonthName, getYear } from '@/lib/utils'

export const INITIAL_HOME_FILTERS: HomeFilters = {
  years: [],
  countries: [],
  primaryFilter: undefined,
}

const getPhotoDates = (date: string) => {
  const dateObj = date ? new Date(date) : null
  const year = dateObj ? getYear(dateObj) : ''
  const month = dateObj ? getMonthName(dateObj) : ''
  return {
    year,
    month,
  }
}

export const formatFilterData = (photos: PhotoPayload[]): HomeFilterData => {
  const data = photos.reduce(
    (acc, { country, date }) => {
      const { year } = date ? getPhotoDates(date) : {}

      if (country) {
        const entry = acc.countries.find((e) => e.name === country)
        if (entry) {
          entry.occurrences += 1
        } else {
          acc.countries.push({ name: country, occurrences: 1 })
        }
      }
      if (year) {
        const entry = acc.years.find((e) => e.name === year)
        if (entry) {
          entry.occurrences += 1
        } else {
          acc.years.push({ name: year, occurrences: 1 })
        }
      }

      return acc
    },
    {
      countries: [] as HomeFilterEntry[],
      years: [] as HomeFilterEntry[],
    },
  )

  // Sort the countries alphabetically
  data.countries.sort((a, b) => a.name.localeCompare(b.name))
  // Sort the years in descending order
  data.years.sort((a, b) => parseInt(b.name) - parseInt(a.name))

  return data
}

export const updateFilterOccurrences = (
  filterData: HomeFilterData,
  photos: PhotoPayload[],
  primaryFilter: HomeFilterCategory | undefined,
): HomeFilterData => {
  if (!photos.length) return filterData
  // Deeep clone the filterData to avoid mutating the original object
  const updatedFilterData = JSON.parse(JSON.stringify(filterData))

  const counts = photos.reduce(
    (acc, { date, country }) => {
      if (date && primaryFilter !== 'years') {
        const year = new Date(date).getFullYear().toString()
        acc.years[year] = (acc.years[year] || 0) + 1
      }
      if (country && primaryFilter !== 'countries') {
        acc.countries[country] = (acc.countries[country] || 0) + 1
      }
      return acc
    },
    {
      years: {} as Record<string, number>,
      countries: {} as Record<string, number>,
    },
  )

  ;(['years', 'countries'] as const).forEach((filter) => {
    if (primaryFilter !== filter) {
      updatedFilterData[filter].forEach((entry: HomeFilterEntry): void => {
        entry.occurrences = counts[filter][entry.name] || 0
      })
    }
  })

  return updatedFilterData
}

export const whichFiltersAreActive = (
  state: HomeFilters,
): HomeFilterCategory | 'both' | false => {
  if (state.years.length > 0 && state.countries.length > 0) return 'both'
  if (state.years.length > 0) return 'years'
  if (state.countries.length > 0) return 'countries'
  return false
}

export const areFiltersAreActive = (state: HomeFilters) => {
  return state.years.length > 0 || state.countries.length > 0
}

export const getPrimaryFilter = (
  prevState: HomeFilters,
  newState: HomeFilters,
): HomeFilterCategory | undefined => {
  const prevPrimaryFilter = prevState.primaryFilter
  const currentActiveFilters = whichFiltersAreActive(newState)

  if (currentActiveFilters === 'both') {
    return prevPrimaryFilter
  }

  if (currentActiveFilters === 'years') {
    return 'years'
  }
  if (currentActiveFilters === 'countries') {
    return 'countries'
  }
  return undefined
}

export const toggleFilter = ({
  state,
  type,
  value,
  action,
}: {
  state: HomeFilters
  type: HomeFilterCategory
  value: string
  action: 'add' | 'remove'
}): HomeFilters => {
  const newState = JSON.parse(JSON.stringify(state))
  if (action === 'add') {
    newState[type].push(value)
  } else {
    newState[type] = newState[type].filter((item: string) => item !== value)
  }
  return newState
}

export const filterPhotos = (
  filterState: HomeFilters,
  photos: PhotoPayload[],
  primaryFilter?: HomeFilterCategory,
): PhotoPayload[] => {
  if (!areFiltersAreActive(filterState)) return []
  return photos.filter((photo) => {
    const year = photo.date ? new Date(photo.date).getFullYear().toString() : ''
    const isYearIncluded =
      filterState.years.length === 0 || filterState.years.includes(year)
    const isCountryIncluded =
      filterState.countries.length === 0 ||
      filterState.countries.includes(photo.country || '')

    // If primary filter is set, we only check that filter
    if (primaryFilter === 'years') return isYearIncluded
    if (primaryFilter === 'countries') return isCountryIncluded

    return isYearIncluded && isCountryIncluded
  })
}
